import _ from "lodash";
import update from "immutability-helper";
import { i18n } from "lib/i18n";
import { getCreateAssessmentInputData } from "Assessments/modules/AssessmentModule";
import {
  dispatchError,
  generateRandomId,
  getCurriculumProgramIdFromState,
} from "Utils";
import {
  getNestedChildren,
  getAncestors,
  isModule,
  isUnitPlan,
  isFolder,
  isAssessment,
  getItemTypeLocale,
  FLOW,
  COURSE_FLOW,
} from "Utils/EntityFlowUtils";
import { getIsUniversalParentEnabled } from "Utils/ACLStoreUtils";
import {
  getCourseFlowItemFromCache,
  getEntireCourseFlowFeedQueryFromCache,
  writeCourseFlowItemInCache,
} from "./CourseFlowGraphqlHelpers";
import {
  getCourseFlowFeed,
  getCourseFlowFeedQueryVariables,
  getCourseFlowOutlineQueryVariables,
  getCourseFlowOutlineFeed,
  getWrapperFieldName,
  SHARE_ACTIONS,
  SHARING_STATES,
  getIsResourceSharedRecursivelyWithSameSettings,
  GENERIC_ITEM_TYPES,
  ITEM_TYPES,
  TOAST_ACTION_LABELS,
  LOCKING_ACTIONS,
  LOCKING_SCOPE,
  getHasConflict,
} from "./Utils";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";
import {
  getEntireCourseFlowFeedQuery,
  getCourseFlowOutlineQuery,
} from "./CourseFlowQueries";
import {
  createCourseFlowResourceMutation,
  deleteCourseFlowResourceMutation,
  updateCourseFlowResourceMutation,
  importCourseFlowResourceMutation,
  repeatCourseFlowResourceMutation,
  reorderCourseFlowResourceMutation,
  shareCourseFlowResourceMutation,
  updateUnitPlanSharingMutation,
  updateLockedStateMutation,
} from "./CourseFlowMutations";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import {
  getSelectedLearningCourseId,
  getLearningCourseVariables,
} from "LearningCourse/modules/Utils";
import { getIsBlueprintSyncHistoryV2Enabled } from "LearningCourse/modules/LearningCourseUtils";
import { copyUnitPlanMutation } from "LearningCourse/modules/LearningCourseMutation";
import {
  getOrganizationId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { MAX_DEPTH_FOR_COURSE_FLOW } from "UIComponents/NestedAccordion/constants";
import {
  getLearningCourseUnitPlanRefetchQueries,
  getLearningCourseUnitPlanFeedQueryVariables,
} from "UnitPlans/modules/UnitPlanModule";
import { getLearningCourseUnitPlanFeedQuery } from "UnitPlans/modules/LearningCourseFlow/UnitPlanLearningCourseQuery";
import ACLStore from "lib/aclStore";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";

const NAME = "CourseFlow";
const SET_ASSIGNMENT_MODAL_DATA = `SET_ASSIGNMENT_MODAL_DATA_${NAME}`;
const SET_ASSESSMENT_IMPORT_MODAL_DATA = `SET_ASSESSMENT_IMPORT_MODAL_DATA_${NAME}`;
const SET_ASSESSMENT_SELECT_MODAL_DATA = `SET_ASSESSMENT_SELECT_MODAL_DATA_${NAME}`;
const SET_IMPORT_FROM_ANOTHER_CLASS = `SET_IMPORT_FROM_ANOTHER_CLASS_${NAME}`;
const SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS = `SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS_${NAME}`;
const SET_IMPORT_FROM_ANOTHER_COURSE_CLASS_MODAL = `SET_IMPORT_FROM_ANOTHER_COURSE_CLASS_MODAL_${NAME}`;
const SET_IMPORT_UNIT_FROM_LIBRARY = `SET_IMPORT_UNIT_FROM_LIBRARY_${NAME}`;
const SET_IMPORT_UNIT_FROM_TODDLE_LIBRARY = `SET_IMPORT_UNIT_FROM_TODDLE_LIBRARY_${NAME}`;
const SET_ADD_NOTE_MODAL_DATA = `SET_ADD_NOTE_MODAL_DATA_${NAME}`;
const SET_WORKSHEET_MODAL_DATA = `SET_WORKSHEET_MODAL_DATA_${NAME}`;
const SET_ADD_LINK_MODAL_DATA = `SET_ADD_LINK_MODAL_DATA_${NAME}`;
const SET_ACTIVE_CONTENT_DATA = `SET_SECTION_CREATION_${NAME}`;
const UPDATE_DISPLAY_SETTINGS = `UPDATE_DISPLAY_SETTINGS_${NAME}`;
const UPDATE_FILTERS = `UPDATE_FILTERS_${NAME}`;
const RESET_FILTERS = `RESET_FILTERS_${NAME}`;
const RESET_MODAL_STATE = `RESET_MODAL_STATE_${NAME}`;
const SET_ACTION_LOADING = `SET_ACTION_LOADING_${NAME}`;
const SET_MOVE_TO_SECTION_MODAL_DATA = `SET_MOVE_TO_SECTION_MODAL_DATA_${NAME}`;
const SET_CONFIRM_MODAL_DATA = `SET_CONFIRM_MODAL_DATA_${NAME}`;
const SET_IS_DRAG_DISABLED = `SET_IS_DRAG_DISABLED_${NAME}`;
const SET_IS_NAVIGATING_FROM_UNIT_FLOW = `SET_IS_NAVIGATING_FROM_UNIT_FLOW_${NAME}`;

// function to update childrenHierarchyInfo of all ancestors (bottom to top) of a resource
const updateAncestorChildCountRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;

  if (node?.childrenHierarchyInfo) {
    node.childrenHierarchyInfo.totalCount += factor;
  }

  if (node?.parent) {
    updateAncestorChildCountRecursively({
      items,
      id: node.parent.id,
      factor,
    });
  }
};

// updates depth recursively
const updateDepthRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;
  node.depth += factor;

  const children = _.get(node, "childrenV2", []);

  _.forEach(children, childNode => {
    updateDepthRecursively({
      items,
      id: childNode.id,
      factor,
    });
  });
};

// recalculates max depth of all ancestors (bottom to top) of a resource
// this is required for drag and drop restrictions
const recalculateMaxDepth = ({ items, id }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const children = _.get(item, "node.childrenV2", []);
  const childrenItems = _.chain(children)
    .map(child => {
      return _.find(items, ["node.id", child.id]);
    })
    .filter(child => Boolean(child))
    .value();

  const maxDepth =
    children.length == 0
      ? 1
      : _.reduce(
          childrenItems,
          (result, { node }) => {
            return Math.max(
              result,
              _.get(node, "childrenHierarchyInfo.maxDepth", 1) + 1
            );
          },
          0
        );

  if (item?.node?.childrenHierarchyInfo) {
    item.node.childrenHierarchyInfo.maxDepth = maxDepth;
  }

  const parent = _.find(items, ["node.id", item.node.parent?.id]);
  if (parent) {
    recalculateMaxDepth({ items, id: parent.node.id });
  }
};

// when dragging an item after a folder, the putAfterItemId needs to be changed
// because we might need to put the new item after the last child of the folder
const interpolatePutAfterId = ({
  items: _items,
  id,
  putAfterItemId: _putAfterItemId,
  depth,
}) => {
  // remove current item from items
  const items = _.filter(_items, item => item.node.id !== id);
  const itemIndex = _.findIndex(items, ["node.id", _putAfterItemId]);
  const item = items[itemIndex];

  if (!item) return _putAfterItemId;

  let putAfterItemId = _putAfterItemId;

  const isModule = item.node.resourceType === "FOLDER";
  if (isModule) {
    const folderDepth = item.node.depth;
    if (depth > folderDepth) {
      putAfterItemId = _putAfterItemId;
    } else {
      let i = itemIndex;
      while (i < items.length) {
        i++;

        const nextItem = items[i];

        const itemDepth = _.get(nextItem, "node.depth");

        if (itemDepth <= depth) {
          const prevItem = items[i - 1];
          const itemId = prevItem?.node?.id;

          putAfterItemId = itemId ?? putAfterItemId;
          break;
        }

        // reached the end, put after the last item
        if (i === items.length - 1) {
          putAfterItemId = nextItem?.node?.id;
          break;
        }
      }
    }
  }

  return putAfterItemId;
};

/**
 * The data in main feed is different than their
 * child nodes, so pick only what is needed
 */
const getChildDataForCache = ({ node }) => {
  // return _.pick(node, ["id", "label", "resourceType", "displaySequence"]);
  return {
    id: node?.id,
  };
};

const removeRepeatedResourcesFromSourceFragment = ({
  itemId,
  variables,
  rollbackResources,
  itemIdsToDelete,
}) => {
  const cachedData = getEntireCourseFlowFeedQueryFromCache(variables);
  const cachedFeed = getCourseFlowFeed(cachedData);

  // this is the source edge from entire feed cache
  const cachedSourceResourceEdge = findSourceResource({
    courseFlowEdges: cachedFeed.edges,
    itemId,
  });

  // this is the source resource from multinode cache which contains item(attachment/assessment) also
  const cachedSourceResource = getCourseFlowItemFromCache({
    id: _.get(cachedSourceResourceEdge, "node.id", ""),
    variables,
  });

  if (cachedSourceResource) {
    // used to rollback on error
    rollbackResources.push(cachedSourceResource);

    const sourceResource = _.cloneDeep(cachedSourceResource);

    // updating the repeated resources of the source resource
    sourceResource.repeatedResources = _.difference(
      sourceResource.repeatedResources,
      itemIdsToDelete
    );

    // updates the source resource in multinode cache
    writeCourseFlowItemInCache({
      id: sourceResource.id,
      variables,
      data: sourceResource,
    });
  }
};

const findSourceResource = ({ courseFlowEdges, itemId }) => {
  return _.find(courseFlowEdges, edge => {
    const { node } = edge;
    return node.isRepeated === false && _.get(node, "item.id") === itemId;
  });
};

// will be used to rollback the resources in cache in case of error
const rollbackResourcesInCache = ({ rollbackResources }) => {
  _.forEach(rollbackResources, resource => {
    writeCourseFlowItemInCache({
      id: resource.id,
      variables: resource.variables,
      data: resource,
    });
  });
};

export const getActionToast = args => {
  const { action, customLabel, itemType, name } = args;
  let customMsg, label;
  switch (itemType) {
    case ITEM_TYPES.UNIT_PLAN: {
      if (action == TOAST_ACTION_LABELS.CREATED) label = "common:new_unit";
      else label = "common:unit";

      if (action == TOAST_ACTION_LABELS.DELETED)
        customMsg = "common:unit_moved_to_bin";
      break;
    }

    case ITEM_TYPES.FOLDER: {
      if (action == TOAST_ACTION_LABELS.CREATED) label = "common:new_section";
      else label = "common:folder";

      break;
    }

    case ITEM_TYPES.LINK: {
      label = "common:link";

      break;
    }

    case ITEM_TYPES.NOTE: {
      label = "common:note";

      break;
    }

    case ITEM_TYPES.FILE: {
      label = "common:resource_label";

      break;
    }

    case ITEM_TYPES.LE: {
      if (action == TOAST_ACTION_LABELS.REMOVED) {
        customMsg = "common:label_removed_from_course_flow";
        label = "common:learning_experience_abbreviation";
      } else if (action == TOAST_ACTION_LABELS.ASSIGNED) {
        customMsg = "common:copy_of_label_assigned_to_course";
        label = "common:learning_experience_abbreviation";
      } else label = "common:task_label";

      break;
    }

    case ITEM_TYPES.ASSESSMENT: {
      if (action == TOAST_ACTION_LABELS.ASSIGNED) {
        customMsg = "common:copy_of_label_assigned_to_course";
        label = name;
      } else label = "common:assessment_label";

      break;
    }
  }

  return setToastMsg({
    msg: customMsg || "toastMsgs:action_successfully",
    locale_params: [
      {
        key: "label",
        value: customLabel || label,
        isPlainText: false,
      },
      {
        key: "action",
        value: action, // created | updated | renamed | deleted | uploaded | duplicated | restored
        isPlainText: false,
      },
      {
        key: "name",
        value: name,
        isPlainText: false,
      },
    ],
    type: "success",
  });
};

export const createCourseFlowResources = ({
  resources,
  putAfterItemId: _putAfterItemId = null,
  setNewDataIds,
  isModuleCollapsed: _isModuleCollapsed = false,
  sectionCreationType,
  showLoader = false,
  parentId,
  newDepth: _newDepth,
  isDuplicating = false,
  isAssessmentImport = false,
  isImportFromUnit = false,
  showToast = true,
  shouldAddToBottom = false,
}) => {
  return async (dispatch, getState) => {
    if (_.isEmpty(resources)) return;

    const state = getState();

    const learningCourseId = getSelectedLearningCourseId(state);
    if (!learningCourseId) return;

    const isInlineCreateOrEdit = _.get(
      state,
      "courseFlow.activeContentData.isInlineCreateOrEditItem",
      false
    );

    const isCreatingAssessment = _.some(resources, {
      itemType: "ASSESSMENT",
    });

    const resource = _.first(resources);

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    if (showLoader) {
      dispatch(setIsActionLoading(true));
    }

    try {
      const cachedData = getEntireCourseFlowFeedQueryFromCache(
        feedQueryVariables
      );
      const courseFlowFeed = _.cloneDeep(getCourseFlowFeed(cachedData));

      let putAfterItemId = _putAfterItemId,
        isModuleCollapsed = _isModuleCollapsed;

      if (isCreatingAssessment) {
        const isInlineCreation = _.get(
          state,
          "courseFlow.activeContentData.isInlineCreateOrEditItem",
          false
        );

        let itemState;
        switch (true) {
          case isInlineCreation:
            itemState = "activeContentData";
            break;
          case isAssessmentImport:
            itemState = "assessmentImportModalData";
            break;
          case isImportFromUnit:
            itemState = "assessmentSelectModalData";
            break;
          default:
            itemState = "assignmentModalData";
        }

        putAfterItemId = _.get(
          state,
          `courseFlow.${itemState}.putAfterItemId`,
          putAfterItemId
        );

        isModuleCollapsed = _.get(
          state,
          `courseFlow.${itemState}.isModuleCollapsed`,
          isModuleCollapsed
        );
      }

      const putAfterItem = _.find(
        courseFlowFeed?.edges,
        ({ node }) => node.id === putAfterItemId
      )?.node;

      const isSection = isModule(putAfterItem);

      // when creating a folder in parallel to current
      const isParallelSection = sectionCreationType === "PARALLEL";

      // when creating a subsection to the current folder which was previously collapsed
      const isExpandedSubsection =
        sectionCreationType === "EXPANDED_SUBSECTION";

      // if putAfterItem is a folder, then newly created resources are meant to go inside it
      // otherwise, we find the parent of putAfterItem
      let parent = putAfterItem;

      const isUniversalParentEnabled = getIsUniversalParentEnabled();
      const putAfterItemDepth = putAfterItem?.depth ?? 0;
      const isUniversalParentEligible =
        isUniversalParentEnabled &&
        !isSection &&
        (_newDepth !== undefined
          ? _newDepth > putAfterItemDepth
          : putAfterItemDepth < MAX_DEPTH_FOR_COURSE_FLOW);

      // if putAfterItem is collapsed, new item should be created after it (as sibling)
      const shouldPutInsideSection =
        (isSection || isUniversalParentEligible) &&
        !isModuleCollapsed &&
        !isParallelSection;

      if (!shouldPutInsideSection) {
        const newParentId =
          isParallelSection || (isExpandedSubsection && parentId)
            ? parentId
            : putAfterItem?.parent?.id;

        parent = _.find(
          courseFlowFeed?.edges,
          ({ node }) => node.id === newParentId
        )?.node;
      }

      const parentResourceId = shouldPutInsideSection
        ? putAfterItemId
        : parent?.id;

      const outlineQueryVariables = getCourseFlowOutlineQueryVariables({
        state,
      });

      const { filters, stateFilters } = feedQueryVariables;
      const defaultPositioningOfNewItems = _.get(
        state,
        "courseFlow.displaySettings.add_to_top",
        false
      );

      const _addToTop = shouldAddToBottom
        ? false
        : defaultPositioningOfNewItems;

      const shouldAddtoTop = _addToTop && !putAfterItemId;

      const variables = {
        input: {
          learningCourseId,
          parentId: parentResourceId || null,
          putAfterItemId,
          resources,
          addToTop: shouldAddtoTop,
        },
        filters,
        stateFilters,
        syncedBluePrintItemFilters: {
          learningCourseId,
        },
      };

      return await client.mutate({
        mutation: createCourseFlowResourceMutation,
        variables,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getCourseFlowOutlineQuery,
            variables: outlineQueryVariables,
          },
        ],
        update: (
          cache,
          {
            data: {
              planner: { createLearningCourseFlowResource },
            },
          }
        ) => {
          if (
            _.isEmpty(courseFlowFeed) ||
            _.isEmpty(createLearningCourseFlowResource)
          )
            return;

          let newDepth = 1;
          if (putAfterItem) {
            newDepth = putAfterItem.depth;
            if (shouldPutInsideSection) newDepth += 1;
          }

          if (isParallelSection && parent.depth) {
            newDepth = parent.depth + 1;
          }

          newDepth = _newDepth ?? newDepth;

          cache.updateQuery(
            {
              query: getEntireCourseFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const createdResources = _.map(
                _.cloneDeep(createLearningCourseFlowResource),
                resource => ({
                  __typename: "LearningCourseFlowEdge",
                  node: {
                    __typename: "LearningCourseFlow",
                    ...resource,
                    ...(isModule(resource) &&
                    _.isEmpty(resource.childrenHierarchyInfo)
                      ? {
                          childrenHierarchyInfo: {
                            maxDepth: 1,
                            totalCount: 0,
                            totalCountWithFolder: 0,
                            __typename: "LearningCourseFlowChildHierarchyInfo",
                          },
                        }
                      : {}),
                    depth: resource.depth ?? newDepth,
                  },
                })
              );

              if (putAfterItemId && parent) {
                const createdResourcesAsChild = _.map(
                  createdResources,
                  resource => {
                    const { node } = resource;

                    return {
                      __typename: "LearningCourseFlow",
                      ...getChildDataForCache({ node }),
                    };
                  }
                );

                parent.childrenV2 = [
                  ...createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                const factor = _.reduce(
                  createdResources,
                  (acc, resource) => acc + (isModule(resource.node) ? 0 : 1),
                  0
                );

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: courseFlowFeed.edges,
                  factor,
                });
              }

              // if put after item is present, then add in edges after the id
              if (putAfterItemId) {
                let index = _.findIndex(
                  courseFlowFeed.edges,
                  ({ node }) => node.id === putAfterItemId
                );

                if (
                  (isSection || isUniversalParentEligible) &&
                  (isModuleCollapsed || putAfterItem.depth >= newDepth)
                ) {
                  const feedLength = courseFlowFeed.edges.length;

                  for (let i = index + 1; i < feedLength; i++) {
                    const item = courseFlowFeed.edges[i];
                    if (item.node.depth <= newDepth) {
                      index = i - 1;
                      break;
                    }

                    // edge case: this is the last folder in the feed
                    if (i === feedLength - 1) {
                      index = i;
                      break;
                    }
                  }
                }

                courseFlowFeed.edges.splice(index + 1, 0, ...createdResources);
              } else {
                if (shouldAddtoTop) {
                  courseFlowFeed.edges = [
                    ...createdResources,
                    ...courseFlowFeed.edges,
                  ];
                } else {
                  courseFlowFeed.edges = [
                    ...courseFlowFeed.edges,
                    ...createdResources,
                  ];
                }
              }

              if (parent) {
                recalculateMaxDepth({
                  items: courseFlowFeed.edges,
                  id: parent.id,
                });
              }

              setNewDataIds?.(
                _.map(createLearningCourseFlowResource, "id", [])
              );

              const updatedEdges = courseFlowFeed.edges;
              const updatedTotalCount =
                _.get(courseFlowFeed, "resourceBasedCount.totalCount", 0) +
                (createdResources?.length || 0);

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                      resourceBasedCount: {
                        totalCount: {
                          $set: updatedTotalCount,
                        },
                      },
                    },
                  },
                },
              });
            }
          );

          setTimeout(() => {
            if (isInlineCreateOrEdit) {
              dispatch(setActiveContentData({}));
            }
            if (!isImportFromUnit && !isAssessmentImport) {
              if (isCreatingAssessment && showToast) {
                const isLe = _.isEqual(
                  "LEARNING_ENGAGEMENT",
                  _.get(resource, "itemInput.assessment.type")
                );

                dispatch(
                  getActionToast({
                    action: TOAST_ACTION_LABELS.CREATED,
                    itemType: isLe ? ITEM_TYPES.LE : ITEM_TYPES.ASSESSMENT,
                  })
                );
              } else {
                const resourceInput = resource?.itemInput;
                const totalResources = _.size(resources);
                const isUploaded = _.has(resourceInput?.attachment, "metadata");

                const resourceItemType = _.get(
                  resourceInput,
                  "attachment.type",
                  ""
                );
                const isLink = resourceItemType === ITEM_TYPES.LINK;
                const isNote = resourceItemType === ITEM_TYPES.NOTE;

                const isUnit = isUnitPlan({ resourceType: resource?.itemType });

                const isSection = isFolder({
                  resourceType: resource?.itemType,
                });

                let itemType;
                if (isUnit) {
                  itemType = ITEM_TYPES.UNIT_PLAN;
                } else if (isSection) {
                  itemType = ITEM_TYPES.FOLDER;
                } else if (isLink) {
                  itemType = ITEM_TYPES.LINK;
                } else if (isNote) {
                  itemType = ITEM_TYPES.NOTE;
                } else {
                  itemType = ITEM_TYPES.FILE;
                }

                const customLabel =
                  totalResources > 1 ? "common:resources" : undefined;

                const action = isDuplicating
                  ? TOAST_ACTION_LABELS.DUPLICATED
                  : isUploaded
                  ? TOAST_ACTION_LABELS.UPLOADED
                  : TOAST_ACTION_LABELS.CREATED;

                dispatch(
                  getActionToast({
                    action,
                    itemType,
                    customLabel,
                  })
                );
              }
            }
          });
        },
      });
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
      dispatch(setIsActionLoading(false));
    }
  };
};

export const deleteCourseFlowResources = ({
  ids: _ids,
  shouldDeleteSectionContents = false,
  repeatedResources = [],
  deleteLinkedCopies,
  isSection,
}) => {
  return async (dispatch, getState) => {
    const ids = _.compact(_ids);
    if (_.isEmpty(ids)) return;

    const state = getState();

    const input = {
      ids,
      removeWithChildren: shouldDeleteSectionContents,
    };

    const refetchQueries = [];

    if (isSection) {
      const outlineQueryVariables = getCourseFlowOutlineQueryVariables({
        state,
      });
      refetchQueries.push({
        query: getCourseFlowOutlineQuery,
        variables: outlineQueryVariables,
      });
    }

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    const cachedData = getEntireCourseFlowFeedQueryFromCache(
      feedQueryVariables
    );

    const cachedFeed = getCourseFlowFeed(cachedData);

    const cachedFeedNodes = _.map(cachedFeed.edges, "node");

    const resource = getCourseFlowItemFromCache({
      id: _.first(ids),
      variables: feedQueryVariables,
    });

    const isRepeatedResource = _.get(resource, "isRepeated", false);

    // pushes all the siblings of the resource to ids
    if (deleteLinkedCopies) {
      const id = _.get(resource, "id", "");
      const resourceItemId = _.get(resource, "item.id", "");

      _.forEach(cachedFeed.edges, ({ node }) => {
        const isRepeated = _.get(node, "isRepeated", false);
        const itemId = _.get(node, "item.id", "");
        if (isRepeated && itemId === resourceItemId && id !== node.id) {
          ids.push(node.id);
        }
      });
    }

    const itemIdsToDelete = [...ids, ...repeatedResources];

    // will contain resources which are going to be roll backed in case of error
    const rollbackResources = [];

    // if the resource is repeated, then we need to update the source resource
    if (isRepeatedResource) {
      removeRepeatedResourcesFromSourceFragment({
        itemId: _.get(resource, "item.id", ""),
        variables: feedQueryVariables,
        rollbackResources,
        itemIdsToDelete,
      });
    }

    if (isModule(resource) && shouldDeleteSectionContents) {
      const deletedItems = getNestedChildren({
        items: cachedFeedNodes,
        id: resource.id,
      });

      const nestedChildrenIds = _.map(deletedItems, "id");

      _.forEach(deletedItems, item => {
        const resourceType = _.get(item, "resourceType", "");

        if (resourceType == "ASSESSMENT") {
          const repeatedResourcesIds = _.get(item, "repeatedResources", []);
          const isRepeated = _.get(item, "isRepeated", false);

          const remainingRepeatedResourcesIds = _.difference(
            repeatedResourcesIds,
            nestedChildrenIds
          );

          // means this is the source assessment with repeatedResources in it
          if (isRepeated) {
            removeRepeatedResourcesFromSourceFragment({
              itemId: _.get(item, "item.id", ""),
              variables: feedQueryVariables,
              rollbackResources,
              itemIdsToDelete: [item.id],
            });
          }

          if (!_.isEmpty(remainingRepeatedResourcesIds)) {
            // take first item as new source as it's the most recent and will become our new source
            const newSourceId = remainingRepeatedResourcesIds.shift();

            const cachedNewSource = getCourseFlowItemFromCache({
              id: newSourceId,
              variables: feedQueryVariables,
            });

            if (cachedNewSource) {
              // used to rollback on error
              rollbackResources.push(cachedNewSource);

              const newSource = _.cloneDeep(cachedNewSource);

              newSource.isRepeated = false;
              newSource.repeatedResources = remainingRepeatedResourcesIds;

              writeCourseFlowItemInCache({
                id: newSourceId,
                variables: feedQueryVariables,
                data: newSource,
              });
            }
          }
        }
      });
    }

    let countOfDeletedItems = 0;

    try {
      const success = await client.mutate({
        mutation: deleteCourseFlowResourceMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            deleteLearningCourseFlowResource: true,
            __typename: "PlannerMutations",
          },
        },
        refetchQueries,
        update: cache => {
          cache.updateQuery(
            {
              query: getEntireCourseFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const CourseFlowFeed = _.cloneDeep(getCourseFlowFeed(data));

              const CourseFlowFeedNodes = _.map(CourseFlowFeed.edges, "node");

              const items = _.filter(CourseFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  CourseFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: CourseFlowFeed.edges,
                    factor: isModule(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: CourseFlowFeed.edges,
                    id: parent.id,
                  });
                }

                const nestedChildren = getNestedChildren({
                  items: CourseFlowFeedNodes,
                  id: item.id,
                });

                if (isModule(item)) {
                  if (shouldDeleteSectionContents) {
                    const ids = _.map(nestedChildren, "id");
                    CourseFlowFeed.edges = _.filter(
                      CourseFlowFeed.edges,
                      ({ node }) => !_.includes(ids, node.id)
                    );

                    if (parent) {
                      updateAncestorChildCountRecursively({
                        id: parent.id,
                        items: CourseFlowFeed.edges,
                        factor: -_.get(
                          item,
                          "childrenHierarchyInfo.totalCount",
                          0
                        ),
                      });
                    }
                  } else {
                    // children should be pushed outwards after parent deletion
                    // so we decrease depth and change parent
                    _.forEach(nestedChildren, child => {
                      child.depth -= 1;

                      // only update parent of immediate children
                      // otherwise the remaining structure will come out flat
                      if (parent && child.parent?.id === item.id) {
                        parent.childrenV2.push(
                          getChildDataForCache({ node: child })
                        );

                        child.parent = {
                          ...(child.parent ?? {}),
                          id: parent.id,
                        };
                      }
                    });
                  }
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isModule(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedEdges = _.filter(
                CourseFlowFeed.edges,
                ({ node }) => !_.includes(itemIdsToDelete, node.id)
              );

              const updatedTotalCount =
                _.get(CourseFlowFeed, "resourceBasedCount.totalCount", 0) -
                countOfDeletedItems;

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                      resourceBasedCount: {
                        totalCount: {
                          $set: updatedTotalCount,
                        },
                      },
                    },
                  },
                },
              });
            }
          );
        },
      });

      if (success) {
        const resourceType = _.get(resource, "resourceType", "");

        let itemType,
          action = TOAST_ACTION_LABELS.DELETED;
        if (isModule(resource)) {
          itemType = resourceType;
        } else if (resourceType === ITEM_TYPES.ASSESSMENT) {
          const isLe = _.isEqual(
            "LEARNING_ENGAGEMENT",
            _.get(resource, "item.type")
          );
          itemType = isLe ? ITEM_TYPES.LE : ITEM_TYPES.ASSESSMENT;

          action = TOAST_ACTION_LABELS.REMOVED;
        } else {
          const attachmentType = _.get(
            resource,
            "item.attachment.attachmentType",
            ""
          );

          itemType = _.includes(
            [ITEM_TYPES.LINK, ITEM_TYPES.NOTE],
            attachmentType
          )
            ? attachmentType
            : ITEM_TYPES.FILE;
        }

        dispatch(
          getActionToast({
            action,
            itemType,
          })
        );
      }
    } catch (error) {
      console.error(error);

      // Revert optimistic data on error
      cache.updateQuery(
        {
          query: getEntireCourseFlowFeedQuery,
          variables: feedQueryVariables,
        },
        () => cachedData
      );

      // Revert resources on error
      rollbackResourcesInCache({ rollbackResources });

      dispatchError({ dispatch, e: error });
    }
  };
};

export const updateCourseFlowResources = ({
  id,
  label,
  attachment,
  resourceType,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const isInlineCreateOrEdit = _.get(
      state,
      "courseFlow.activeContentData.isInlineCreateOrEditItem",
      false
    );

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    const outlineQueryVariables = getCourseFlowOutlineQueryVariables({
      state,
    });

    const variables = {
      input: {
        id,
        label,
      },
    };

    if (attachment) {
      if (_.includes(["LINK", "NOTE"], resourceType)) {
        variables.input.attachment = attachment;
      } else {
        variables.input.attachment = {
          type: _.get(attachment, "type", "FILE"),
          name: label,
        };
      }
    }

    const cachedData = getEntireCourseFlowFeedQueryFromCache(
      feedQueryVariables
    );

    const courseFlowFeed = _.cloneDeep(getCourseFlowFeed(cachedData).edges);

    const originalNode = _.find(courseFlowFeed, ({ node }) => node.id === id);
    const itemId = _.get(originalNode, "node.item.id", "");

    const rollbackResources = [];
    if (attachment) {
      _.forEach(courseFlowFeed, edge => {
        const { node } = edge;
        if (node.item?.id !== itemId) return;

        const cachedCourseFlowNode = getCourseFlowItemFromCache({
          id: node.id,
          variables: feedQueryVariables,
        });

        if (cachedCourseFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedCourseFlowNode);

          const courseFlowNode = _.cloneDeep(cachedCourseFlowNode);

          courseFlowNode.label = label;

          if (courseFlowNode?.item) {
            const nodeAttachment = courseFlowNode.item;
            nodeAttachment.name = label;
            if (nodeAttachment.attachmentType === "LINK") {
              nodeAttachment.url = attachment.url;
              nodeAttachment.thumbUrl = attachment.thumbUrl;
            } else if (nodeAttachment.attachmentType === "NOTE") {
              nodeAttachment.name = label;
              nodeAttachment.attachmentTitle = label;
              nodeAttachment.title = label;
            } else {
              nodeAttachment.name = label;
            }
          }

          writeCourseFlowItemInCache({
            id: node.id,
            variables: feedQueryVariables,
            data: courseFlowNode,
          });
        }
      });
    } else {
      const cachedCourseFlowNode = getCourseFlowItemFromCache({
        id,
        variables: feedQueryVariables,
      });

      if (cachedCourseFlowNode) {
        // used to rollback on error
        rollbackResources.push(cachedCourseFlowNode);

        const courseFlowNode = _.cloneDeep(cachedCourseFlowNode);

        courseFlowNode.label = label;
        if (isUnitPlan(courseFlowNode)) {
          courseFlowNode.item.title.value = label;
        }

        writeCourseFlowItemInCache({
          id: id,
          variables: feedQueryVariables,
          data: courseFlowNode,
        });
      }
    }

    try {
      const cachedData = getEntireCourseFlowFeedQueryFromCache(
        feedQueryVariables
      );

      const courseFlowFeed = _.cloneDeep(getCourseFlowFeed(cachedData));

      const originalNode = _.find(
        courseFlowFeed.edges,
        ({ node }) => node.id === id
      );
      const itemId = _.get(originalNode, "node.item.id", "");

      dispatch(setActiveContentData({}));

      await client.mutate({
        mutation: updateCourseFlowResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateLearningCourseFlowResource: {
              id,
              __typename: "LearningCourseFlow",
            },
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getEntireCourseFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(courseFlowFeed.edges, edge => {
                const { node } = edge;
                if (node.item?.id == itemId) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          const isModule = _.includes(["UNIT_PLAN", "FOLDER"], resourceType);
          if (isModule) {
            cache.updateQuery(
              {
                query: getCourseFlowOutlineQuery,
                variables: outlineQueryVariables,
              },
              data => {
                if (!data) return;

                const courseFlowFeedOutline = getCourseFlowOutlineFeed(data);
                const itemIdx = _.findIndex(courseFlowFeedOutline, { id });

                const item = courseFlowFeedOutline[itemIdx];
                const newItem = _.cloneDeep(item);

                if (!newItem) return;
                newItem.label = label;

                return update(data, {
                  node: {
                    learningCourseFlowFeedWrapper: {
                      learningCourseFlowFeedOutline: {
                        [itemIdx]: { $set: newItem },
                      },
                    },
                  },
                });
              }
            );
          }
        },
      });

      const action = _.includes(
        [ITEM_TYPES.LINK, ITEM_TYPES.NOTE],
        resourceType
      )
        ? TOAST_ACTION_LABELS.UPDATED
        : TOAST_ACTION_LABELS.RENAMED;

      dispatch(
        getActionToast({
          action,
          itemType: resourceType,
        })
      );
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
    }
  };
};

export const duplicateUnitPlan = args => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo();

    const { label, item, grades, addToTop = false } = args;
    const { id, startDate, endDate, owner, defaultStaffAccess } = item;
    const curriculumProgramId = getCurriculumProgramIdFromState({ state });
    const learningCourseId = getSelectedLearningCourseId(state);
    const organizationId = getOrganizationId(state);
    const academicYearId = getSelectedAcademicYearId(state);
    const academicYearIds = _.compact([academicYearId]);

    const ownerId = _.get(owner, "id", "");
    const staffId = _.get(userInfo, "id", "");

    const isIncludeReflectionInUnitImportEnabled = ACLStore.can(
      "FeatureFlag:IncludeReflectionInUnitImport"
    );

    const isIncludeReflectionsOptionSelected = isIncludeReflectionInUnitImportEnabled
      ? _.get(state, "unitPlans.isIncludeReflectionsOptionSelected", false)
      : false;

    const input = {
      id,
      copiedBy: staffId,
      title: label,
      learningCourseId,
      ownerId,
      startDate,
      endDate,
      state: item.state,
      defaultStaffAccess,
      curriculumProgramId,
      sourceOrganizationId: organizationId,
      grades,
      academicYears: academicYearIds,
      copyReflection: isIncludeReflectionsOptionSelected,
      addToTop,
    };
    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });
    const outlineQueryVariables = getCourseFlowOutlineQueryVariables({
      state,
    });
    dispatch(setIsActionLoading(true));

    try {
      const { data } = await client.mutate({
        mutation: copyUnitPlanMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getEntireCourseFlowFeedQuery,
            variables: feedQueryVariables,
          },
          {
            query: getCourseFlowOutlineQuery,
            variables: outlineQueryVariables,
          },
        ],
      });
      return data;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      dispatch(setIsActionLoading(false));
    }
  };
};
export const duplicateCourseFlowResources = args => {
  return async (dispatch, getState) => {
    const state = getState();

    const { item, label, setNewDataIds } = args;

    if (!item) return;

    const id = item.id;

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    const cachedData = getEntireCourseFlowFeedQueryFromCache(
      feedQueryVariables
    );
    const cachedFeed = getCourseFlowFeed(cachedData);

    const parentId = _.get(item, "parent.id");
    const putAfterItemId = id;

    const learningCourseId = getSelectedLearningCourseId(state);
    if (!learningCourseId) return;

    const curriculumProgramId = getCurriculumProgramIdFromState({ state });

    const academicYearId = getSelectedAcademicYearId(state);
    const academicYearIds = _.compact([academicYearId]);

    const input = {
      sourceId: learningCourseId,
      destinationId: learningCourseId,
      includedIds: [id],
      parentId,
      putAfterItemId,
      curriculumProgramId,
      academicYearIds,
      label,
      isDuplicateResource: true,
    };

    const { filters, stateFilters } = feedQueryVariables;

    dispatch(setIsActionLoading(true));

    try {
      const courseFlowFeed = _.cloneDeep(cachedFeed);
      const { data } = await client.mutate({
        mutation: importCourseFlowResourceMutation,
        variables: {
          input,
          filters,
          stateFilters,
          syncedBluePrintItemFilters: {
            learningCourseId,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { importLearningCourseFlowResource },
            },
          }
        ) => {
          if (!importLearningCourseFlowResource.isSuccess) return;
          const resource = _.get(
            importLearningCourseFlowResource,
            "courseFlowResource",
            {}
          );

          cache.updateQuery(
            {
              query: getEntireCourseFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const repeatedItem = {
                __typename: "LearningCourseFlowEdge",
                node: resource,
              };

              const parent = _.find(
                courseFlowFeed.edges,
                ({ node }) => node.id === parentId
              )?.node;

              if (parent) {
                const { node } = repeatedItem;
                const createdResourcesAsChild = {
                  __typename: "LearningCourseFlow",
                  ...getChildDataForCache({ node }),
                };

                parent.childrenV2 = [
                  createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: courseFlowFeed.edges,
                  factor: 1,
                });
              }

              const itemIndex = _.findIndex(courseFlowFeed.edges, [
                "node.id",
                putAfterItemId,
              ]);

              courseFlowFeed.edges.splice(itemIndex + 1, 0, repeatedItem);

              setNewDataIds?.([resource.id]);

              const updatedEdges = courseFlowFeed.edges;
              const updatedTotalCount =
                _.get(courseFlowFeed, "resourceBasedCount.totalCount", 0) + 1;

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                      resourceBasedCount: {
                        totalCount: {
                          $set: updatedTotalCount,
                        },
                      },
                    },
                  },
                },
              });
            }
          );
        },
      });

      const response = _.get(
        data,
        "planner.importLearningCourseFlowResource",
        false
      );

      if (response) {
        const { isSuccess } = response;

        if (!isSuccess) {
          dispatch(
            setToastMsg({
              msg: "toastMsgs:something_went_wrong",
              position: "toast-bottom-left",
            })
          );
        } else {
          const resourceType = _.get(item, "resourceType", "");

          let itemType;
          if (resourceType === ITEM_TYPES.ASSESSMENT) {
            const isLe = _.isEqual(
              "LEARNING_ENGAGEMENT",
              _.get(item, "item.type")
            );
            itemType = isLe ? ITEM_TYPES.LE : ITEM_TYPES.ASSESSMENT;
          } else {
            const attachmentType = _.get(item, "item.attachmentType", "");

            itemType = _.includes(
              [ITEM_TYPES.LINK, ITEM_TYPES.NOTE],
              attachmentType
            )
              ? attachmentType
              : ITEM_TYPES.FILE;
          }

          dispatch(
            getActionToast({
              action: TOAST_ACTION_LABELS.DUPLICATED,
              itemType,
            })
          );
        }
      }

      return data;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      dispatch(setIsActionLoading(false));
    }
  };
};

export const repeatCourseFlowResource = args => {
  return async (dispatch, getState) => {
    const state = getState();

    const { item, setNewDataIds } = args;
    const id = _.get(item, "id", "");
    const parentId = _.get(item, "parent.id", null);
    const putAfterItemId = id;
    if (!id) return;

    const learningCourseId = getSelectedLearningCourseId(state);
    if (!learningCourseId) return;

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    const input = {
      id,
      parentId,
      putAfterItemId,
    };

    const cachedData = getEntireCourseFlowFeedQueryFromCache(
      feedQueryVariables
    );

    const cachedFeed = getCourseFlowFeed(cachedData);

    const cachedParent = getCourseFlowItemFromCache({
      id,
      variables: feedQueryVariables,
    });

    const optimisticNode = {
      ..._.omit(item, "attachment"),
      id: generateRandomId(),
      parent: cachedParent,
      isRepeated: true,
      childrenV2: [],
      repeatedResources: [],
      displaySequence: 0,
      depth: null,
      lockingInfo: null,
      isSyncedFromBluePrintCourse: false,
      isSyncedWithAllTeacherCourseV2: {},
      item: {
        ..._.get(item, "item", {}),
        type: "",
      },
    };

    const { filters, stateFilters } = feedQueryVariables;

    /*
      Writes the optimistic node in cache
      Prevents skeleton in case of optimstic response
    */
    writeCourseFlowItemInCache({
      id: optimisticNode.id,
      variables: feedQueryVariables,
      data: optimisticNode,
    });

    try {
      await client.mutate({
        mutation: repeatCourseFlowResourceMutation,
        variables: {
          input,
          filters,
          stateFilters,
          syncedBluePrintItemFilters: {
            learningCourseId,
          },
        },
        optimisticResponse: {
          planner: {
            repeatLearningCourseFlowResource: {
              isSuccess: true,
              error: "",
              courseFlowResource: optimisticNode,
              __typename: "LearningCourseFlowResourceSuccessErrorResponse",
            },
            __typename: "PlannerMutations",
          },
          __typename: "Mutation",
        },
        update: (
          cache,
          {
            data: {
              planner: { repeatLearningCourseFlowResource },
            },
          }
        ) => {
          if (
            _.isEmpty(cachedFeed) ||
            _.isEmpty(repeatLearningCourseFlowResource)
          )
            return;

          const resource = _.get(
            repeatLearningCourseFlowResource,
            "courseFlowResource",
            {}
          );

          const courseFlowFeed = _.cloneDeep(cachedFeed);

          cache.updateQuery(
            {
              query: getEntireCourseFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const repeatedItem = {
                __typename: "LearningCourseFlowEdge",
                node: resource,
              };

              const parent = _.cloneDeep(cachedParent);
              if (parent) {
                const { node } = repeatedItem;
                const createdResourcesAsChild = {
                  __typename: "LearningCourseFlow",
                  ...getChildDataForCache({ node }),
                };

                parent.childrenV2 = [
                  createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: courseFlowFeed.edges,
                  factor: 1,
                });
              }

              const sourceResourceEdge = findSourceResource({
                courseFlowEdges: courseFlowFeed.edges,
                itemId: _.get(item, "item.id"),
              });

              if (sourceResourceEdge) {
                const repeatedItemId = _.get(repeatedItem, "node.id", "");
                sourceResourceEdge.node.repeatedResources.push(repeatedItemId);
              }

              const itemIndex = _.findIndex(courseFlowFeed.edges, [
                "node.id",
                putAfterItemId ?? item.id,
              ]);

              courseFlowFeed.edges.splice(itemIndex + 1, 0, repeatedItem);

              const updatedEdges = courseFlowFeed.edges;
              const updatedTotalCount =
                _.get(courseFlowFeed, "resourceBasedCount.totalCount", 0) + 1;

              setNewDataIds?.([_.get(repeatedItem, "node.id", "")]);
              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                      resourceBasedCount: {
                        totalCount: {
                          $set: updatedTotalCount,
                        },
                      },
                    },
                  },
                },
              });
            }
          );
        },
      });

      const assessmentLabel = _.get(item, "item.title.value", "");

      dispatch(
        setToastMsg({
          type: "info",
          msg: "toastMsgs:connected_view_added_to_course_flow",
          hasHTMLTags: true,
          showIcon: false,
          locale_params: [
            {
              key: "label",
              value: assessmentLabel,
              isPlainText: true,
            },
          ],
        })
      );
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

export const reorderCourseFlowResources = ({
  id,
  putAfterItemId,
  parentId,
  depth,
  isMovedByDragAndDrop,
}) => {
  return async (dispatch, getState) => {
    if (id == putAfterItemId || id == parentId) {
      // Dropping item to it's original position
      return;
    }
    const state = getState();
    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    let switchParent;
    let resourceType;

    try {
      cache.updateQuery(
        {
          query: getEntireCourseFlowFeedQuery,
          variables: feedQueryVariables,
        },
        data => {
          if (!data) return;

          const courseFlowFeed = _.cloneDeep(getCourseFlowFeed(data));

          const courseFlowFeedNodes = _.map(courseFlowFeed.edges, "node");

          const item = _.find(
            courseFlowFeed.edges,
            ({ node }) => node.id === id
          );
          if (!item) return;

          resourceType = _.get(item, "node.resourceType", "");

          // modify putAfterItemId to reflect the correct position in case of folders
          const _putAfterItemId = interpolatePutAfterId({
            id,
            putAfterItemId,
            items: courseFlowFeed.edges,
            depth,
          });

          // remove the item from its current position and place after putAfterItem
          const newEdges = _.reduce(
            courseFlowFeed.edges,
            (result, edge, i) => {
              // item was dragged and dropped to the first position
              if (i === 0 && !_putAfterItemId) {
                result.push(item);
                result.push(edge);
                return result;
              }

              if (edge.node.id === id) return result;

              // put it after putAfterItem
              if (edge.node.id === _putAfterItemId) {
                result.push(edge);
                result.push(item);
              } else {
                result.push(edge);
              }
              return result;
            },
            []
          );

          courseFlowFeed.edges = newEdges;

          let lastItemId = item.node.id;

          // also move children after the item
          const nestedChildren = getNestedChildren({
            items: _.map(courseFlowFeed.edges, "node"),
            id,
          });

          _.forEach(nestedChildren, child => {
            const childIndex = _.findIndex(
              courseFlowFeed.edges,
              ({ node }) => node.id === child.id
            );

            if (childIndex !== -1) {
              const childItem = _.first(
                courseFlowFeed.edges.splice(childIndex, 1)
              );

              const indexToMove = _.findIndex(courseFlowFeed.edges, {
                node: { id: lastItemId },
              });

              courseFlowFeed.edges.splice(indexToMove + 1, 0, childItem);

              lastItemId = child.id;
            }
          });

          const currentParentId = _.get(item, "node.parent.id");
          switchParent = parentId !== currentParentId;

          // adding this condition for null and undefined comparisons
          if (_.isEmpty(currentParentId) && _.isEmpty(parentId)) {
            switchParent = false;
          }

          // remove from current parent's children and put in new parent's children
          if (switchParent) {
            const currentParent = _.find(courseFlowFeedNodes, {
              id: currentParentId,
            });

            if (currentParent) {
              currentParent.childrenV2 = _.filter(
                currentParent.childrenV2,
                ({ id }) => id !== id
              );
              updateAncestorChildCountRecursively({
                id: currentParent.id,
                items: courseFlowFeed.edges,
                factor: -(
                  (isModule(item.node) ? 0 : 1) +
                  _.get(item, "node.childrenHierarchyInfo.totalCount", 0)
                ),
              });

              recalculateMaxDepth({
                items: courseFlowFeed.edges,
                id: currentParent.id,
              });
            }

            if (parentId) {
              item.node.parent = {
                ...(item.node.parent ?? {}),
                id: parentId,
              };

              const parent = _.find(courseFlowFeedNodes, { id: parentId });
              if (parent) {
                parent.childrenV2.push(_.get(item, "node"));
                updateAncestorChildCountRecursively({
                  id: parentId,
                  items: courseFlowFeed.edges,
                  factor:
                    (isModule(item.node) ? 0 : 1) +
                    _.get(item, "node.childrenHierarchyInfo.totalCount", 0),
                });
              }
            } else item.node.parent = null;
          }

          const currentDepth = _.get(item, "node.depth", 1);
          const factor = depth - currentDepth;

          updateDepthRecursively({
            items: courseFlowFeed.edges,
            id: item.node.id,
            factor,
          });

          recalculateMaxDepth({
            items: courseFlowFeed.edges,
            id: item.node.id,
          });

          return update(data, {
            node: {
              learningCourseFlowFeedWrapper: {
                learningCourseFlowFeed: {
                  edges: { $set: courseFlowFeed.edges },
                },
              },
            },
          });
        }
      );

      dispatch(setMoveToSectionModalData({}));

      const outlineQueryVariables = getCourseFlowOutlineQueryVariables({
        state,
      });

      await client.mutate({
        mutation: reorderCourseFlowResourceMutation,
        variables: {
          input: {
            id,
            putAfterItemId,
            parentId,
            switchParent,
          },
        },
        refetchQueries: [
          {
            query: getCourseFlowOutlineQuery,
            variables: outlineQueryVariables,
          },
        ],
      });

      if (!isMovedByDragAndDrop) {
        let title;
        switch (resourceType) {
          case "FOLDER":
            title = "common:folder";
            break;
          case "ASSIGNMENT":
          case "ASSESSMENT": {
            title = "toastMsgs:assignment";
            break;
          }
          default:
            title = `toastMsgs:resource`;
        }

        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:label_successfully_moved",
            locale_params: [
              {
                key: "label",
                value: title,
                isPlainText: false,
              },
            ],
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

export const handleQuickCreateAndEdit = ({
  item,
  isSaving = false,
  setNewDataIds,
  templateId,
  taskTypeId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const academicYearId = getSelectedAcademicYearId(state);
    const {
      type,
      putAfterItemId,
      label,
      isModuleCollapsed,
      sectionCreationType,
      parent,
      depth,
      shouldAddToBottom = false,
    } = item;
    if (isSaving) {
      dispatch(
        setActiveContentData({
          ...item,
          isSavingInline: true,
        })
      );

      const parentId = parent?.id;

      switch (type) {
        case "CREATE_SECTION": {
          dispatch(
            createCourseFlowResources({
              putAfterItemId,
              setNewDataIds,
              resources: [
                {
                  label,
                  itemType: "FOLDER",
                },
              ],
              isModuleCollapsed,
              sectionCreationType,
              parentId,
              newDepth: depth,
              shouldAddToBottom,
            })
          );

          break;
        }

        case "FILE_RENAME":
        case "UNIT_RENAME":
        case "SECTION_RENAME": {
          dispatch(
            updateCourseFlowResources({
              id: item.id,
              label,
              resourceType:
                type == "FILE_RENAME"
                  ? "FILE"
                  : type == "UNIT_RENAME"
                  ? "UNIT_PLAN"
                  : "FOLDER",
              attachment: _.get(item, "attachment", null),
            })
          );
          break;
        }
        default: {
          const learningCourseId = getSelectedLearningCourseId(state);
          const learningCourseVariables = getLearningCourseVariables({
            learningCourseId,
            academicYearId,
          });
          const learningCourse = getLearningCourseDetailsFromCache(
            learningCourseVariables
          );
          const learningCourseGrades = learningCourse?.grades;

          const primaryGradeId = _.first(learningCourseGrades)?.id;

          try {
            const assessmentType = _.get(item, "item.assessmentType", {});
            const groupType = _.get(item, "item.type", null);
            const fields = [];

            const feedQueryVariables = getCourseFlowFeedQueryVariables({
              state,
            });

            const cachedData = getEntireCourseFlowFeedQueryFromCache(
              feedQueryVariables
            );

            const cachedFeed = _.get(
              getCourseFlowFeed(cachedData),
              "edges",
              []
            );
            const feedData = _.map(cachedFeed, "node");

            let ancestorId = parentId;

            if (parentId) {
              const ancestors = getAncestors({
                items: feedData,
                id: parentId,
              });

              const ancestor = _.first(ancestors);
              if (ancestor) {
                ancestorId = ancestor.id;
              }
            }

            const ancestorItemId = _.get(
              _.find(feedData, { id: ancestorId }),
              "item.id",
              -1
            );

            const assessment = await dispatch(
              getCreateAssessmentInputData({
                assessmentType: _.get(assessmentType, "value", "le"),
                title: item.label,
                groupType,
                unitPlanObj: {
                  unitPlanId: ancestorItemId ?? -1,
                },
                fields,
                primaryGradeId,
                learningCourseId,
                templateId,
                taskTypeId,
              })
            );

            if (assessment) {
              return dispatch(
                createCourseFlowResources({
                  putAfterItemId,
                  setNewDataIds,
                  resources: [
                    {
                      label,
                      itemType: "ASSESSMENT",
                      itemInput: {
                        assessment,
                      },
                    },
                  ],
                  isModuleCollapsed,
                  sectionCreationType,
                  parentId,
                  newDepth: depth,
                  showLoader: false,
                  shouldAddToBottom,
                })
              );
            }
          } catch (e) {
            dispatch(setActiveContentData({}));
            console.error(e);
          }
        }
      }
    } else {
      dispatch(setActiveContentData({}));
    }
  };
};

export const importCourseFlowResources = ({
  input,
  shouldAddToBottom = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    dispatch(setIsActionLoading(true));

    const { filters, stateFilters } = feedQueryVariables;
    const learningCourseId = getSelectedLearningCourseId(state);
    if (!learningCourseId) return;

    const putAfterItemId = _.get(input, "putAfterItemId", null);
    const defaultPositioningOfNewItems = _.get(
      state,
      "courseFlow.displaySettings.add_to_top",
      false
    );

    const _addToTop = shouldAddToBottom ? false : defaultPositioningOfNewItems;

    const shouldAddtoTop = _addToTop && !putAfterItemId;

    input.addToTop = shouldAddtoTop;

    try {
      const { data } = await client.mutate({
        mutation: importCourseFlowResourceMutation,
        variables: {
          input,
          filters,
          stateFilters,
          syncedBluePrintItemFilters: {
            learningCourseId,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: dispatch(getLearningCourseUnitPlanRefetchQueries()),
      });

      const response = _.get(
        data,
        "planner.importLearningCourseFlowResource",
        null
      );

      if (response) {
        const { isSuccess, error } = response;
        if (isSuccess) {
          // Might check later if we need to show a toast message
          // dispatch(
          //   setToastMsg({
          //     msg: "classMaterials:toast_msg_for_import_items",
          //     locale_params: [
          //       {
          //         key: "importCount",
          //         value: selectedCount,
          //         isPlainText: true,
          //       },
          //     ],
          //     type: "success",
          //     position: "toast-bottom-left",
          //   })
          // );
        } else {
          if (error == "IMPORT_DEPTH_ERROR") {
            dispatch(
              setToastMsg({
                msg: "classMaterials:disabled_overlay_text",
                locale_params: [
                  {
                    key: "maxDepth",
                    value: MAX_DEPTH_FOR_COURSE_FLOW,
                    isPlainText: true,
                  },
                ],
                position: "toast-bottom-left",
              })
            );
          } else {
            dispatch(
              setToastMsg({
                msg: "toastMsgs:something_went_wrong",
                position: "toast-bottom-left",
              })
            );
          }
        }
      }

      return response;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      dispatch(setIsActionLoading(false));
    }
  };
};

const getSharingToastMsg = ({
  newSharingState = SHARING_STATES.PUBLISHED,
  oldSharingState = SHARING_STATES.DRAFT,
  visibilityStats = true,
  addedClasses = [],
  deletedClasses = [],
  updatedClasses = [],
  shareParentOnly = true,
  resourceType = GENERIC_ITEM_TYPES.RESOURCE,
  associatedClasses = [],
  hasLockedAcademicTermChildren = false,
}) => {
  const t = i18n.t;
  const courseClassesCount = _.size(associatedClasses);
  const addedClassesCount = _.size(addedClasses);
  const deletedClassesCount = _.size(deletedClasses);
  const updatedClassesCount = _.size(updatedClasses);
  const itemsSkippedText =
    resourceType === GENERIC_ITEM_TYPES.UNIT_PLAN ||
    resourceType === GENERIC_ITEM_TYPES.FOLDER
      ? t("course:locked_grading_periond_items_were_skipped")
      : null;

  if (
    !shareParentOnly &&
    addedClassesCount === 0 &&
    deletedClassesCount === 0 &&
    updatedClassesCount === 0
  ) {
    let msg = t("course:children_settings_updated");
    if (hasLockedAcademicTermChildren && itemsSkippedText) {
      msg = `${msg} ${itemsSkippedText}`;
    }

    return msg;
  }

  // Determines whether the action is sharing or unsharing
  let isSharing =
    newSharingState === SHARING_STATES.PUBLISHED &&
    newSharingState !== oldSharingState;
  let isUnsharing =
    newSharingState === SHARING_STATES.DRAFT &&
    newSharingState !== oldSharingState;

  // Check if the item share settings are updating or not
  if (!isSharing && !isUnsharing) {
    if (
      addedClassesCount > 0 &&
      deletedClassesCount === 0 &&
      updatedClassesCount === 0
    ) {
      isSharing = true;
      isUnsharing = false;
    }

    if (
      deletedClassesCount > 0 &&
      addedClassesCount === 0 &&
      updatedClassesCount === 0
    ) {
      isSharing = false;
      isUnsharing = true;
    }
  }

  const isUpdating = !isSharing && !isUnsharing;

  // Retrieves resource type details and the appropriate message locale
  const getResourceTypeDetails = () => {
    const isUnit = resourceType === GENERIC_ITEM_TYPES.UNIT_PLAN;
    const isFolder = resourceType === GENERIC_ITEM_TYPES.FOLDER;
    const isAssessment = resourceType === GENERIC_ITEM_TYPES.ASSESSMENT;

    let type = t("common:resource_label");
    let msgLocale = "course:item_updated_with_action";

    if (isUnit) {
      type = t("common:unit");
      if (!shareParentOnly)
        msgLocale = "course:item_and_its_content_updated_with_action";
    } else if (isFolder) {
      type = t("common:folder");
      if (!shareParentOnly)
        msgLocale = "course:item_and_its_content_updated_with_action";
    } else if (isAssessment) {
      type = t("common:assessment");
    }

    if (isUpdating) {
      msgLocale = "course:item_access_updated";
    }

    return { type, msgLocale };
  };

  // Generates a localized string for the number of classes
  const getClassText = (count, isAll) => {
    if (isAll) {
      return `${t("common:all_lower_case")} ${t(
        "common:class_lowercase_other"
      )}`;
    }

    return t("common:shared_with_class_count", { count });
  };

  const { type, msgLocale } = getResourceTypeDetails();
  const actionText = t(isSharing ? "common:shared" : "common:unshared");

  let msg,
    notVisibleText,
    classText,
    updatedEntitiesText,
    subActionText = t("common:modified");

  if (isSharing) {
    // Handles the case where the item is being shared
    const isSharingWithAll = addedClassesCount === courseClassesCount;
    classText = getClassText(addedClassesCount, isSharingWithAll);

    // Adds visibility information if applicable
    if (_.isObject(visibilityStats)) {
      const parentType = _.get(visibilityStats, "parentType", "");
      const parentText =
        parentType === "UNIT_PLAN" ? t("common:unit") : t("common:folder");

      notVisibleText = t("course:item_shared_but_not_visible", {
        type,
        parentType: parentText,
      });
    }
  } else if (isUnsharing) {
    // Handles the case where the item is being unshared
    const isUnsharingWithAll = deletedClassesCount === courseClassesCount;
    classText = getClassText(deletedClassesCount, isUnsharingWithAll);
  } else {
    // Handle the case where the item share settings are updated

    // Count of all the added and removed students from all the updated classes
    const updatedStudentsCount = _.reduce(
      updatedClasses,
      (acc, cls) => {
        const addedStudents = _.get(cls, "addedStudents", []);
        const removedStudents = _.get(cls, "removedStudents", []);

        return acc + addedStudents.length + removedStudents.length;
      },
      0
    );

    // Count of all the added and removed classes
    const allUpdatedClassesCount = addedClassesCount + deletedClassesCount;

    const updatedEntities = [];

    if (allUpdatedClassesCount > 0) {
      const classesLocale =
        allUpdatedClassesCount > 1
          ? "common:class_lowercase_other"
          : "common:class_lowercase";
      updatedEntities.push(t(classesLocale));
    }

    if (updatedStudentsCount > 0) {
      const studentsLocale =
        updatedStudentsCount > 1
          ? "common:student_lowercase_other"
          : "common:student_lowercase";
      updatedEntities.push(t(studentsLocale));
    }

    // No classes or students were updated, then the unit-planner settings were updated
    if (updatedEntities.length === 0) {
      const studentsLocale = "common:student_lowercase_other";
      updatedEntities.push(t(studentsLocale));
      subActionText = t("common:selected");
    }

    updatedEntitiesText = updatedEntities.join(` ${t("common:and")} `);

    // Adds visibility information if applicable
    if (_.isObject(visibilityStats)) {
      const parentType = _.get(visibilityStats, "parentType", "");
      const parentText =
        parentType === "UNIT_PLAN" ? t("common:unit") : t("common:folder");

      notVisibleText = t("course:item_shared_but_not_visible", {
        type,
        parentType: parentText,
      });
    }
  }

  msg = t(msgLocale, {
    type,
    action: actionText,
    classText,
    updatedEntitiesText,
    subAction: subActionText,
  });

  if (notVisibleText) {
    msg = `${msg} ${notVisibleText}`;
  }

  if (hasLockedAcademicTermChildren && itemsSkippedText) {
    msg = `${msg} ${itemsSkippedText}`;
  }

  return msg;
};

const getSharedClassStudentStructure = ({ details }) => {
  const sharedDetails = _.reduce(
    details,
    (acc, studentIds, classId) => {
      const clsDetails = {
        class: {
          id: classId,
          __typename: "Course",
        },
        students: _.map(studentIds, studentId => ({
          id: studentId,
          __typename: "Student",
        })),
        __typename: "LearningCourseFlowSharedClassStudentMap",
      };
      acc.push(clsDetails);
      return acc;
    },
    []
  );

  return sharedDetails;
};

const updateSharedDetails = ({
  currentSharedDetails,
  addedClasses,
  updatedClasses,
  deletedClasses,
}) => {
  const newSharedSettings = _.reduce(
    currentSharedDetails,
    (acc, cls) => {
      acc[cls.class.id] = _.map(cls.students, "id");
      return acc;
    },
    {}
  );

  // Filter out deleted classes
  _.forEach(deletedClasses, cls => {
    delete newSharedSettings[cls];
  });

  // Update existing classes
  _.forEach(updatedClasses, cls => {
    const classId = cls.classId;
    const sharedClassStudentIds = _.get(newSharedSettings, classId, []);

    // Add/Remove student only if class exists in the current shared details
    if (_.size(sharedClassStudentIds) > 0) {
      const selectedStudents = new Set(sharedClassStudentIds);
      const addedStudents = _.get(cls, "addedStudents", []);
      const removedStudents = _.get(cls, "removedStudents", []);

      _.forEach(addedStudents, studentId => {
        selectedStudents.add(studentId);
      });

      _.forEach(removedStudents, studentId => {
        selectedStudents.delete(studentId);
      });

      newSharedSettings[classId] = Array.from(selectedStudents);
    }
  });

  // Add new classes
  _.forEach(addedClasses, cls => {
    const classId = cls.classId;
    const selectedStudents = _.get(newSharedSettings, classId, []);
    const newStudents = _.get(cls, "studentIds", []);
    const studentIds = new Set([...selectedStudents, ...newStudents]);

    newSharedSettings[classId] = Array.from(studentIds);
  });

  return getSharedClassStudentStructure({ details: newSharedSettings });
};

const getUpdatedChildrenSharingState = ({
  currentSharedDetails = [],
  newSharedDetails = [],
  editableClassIds = [],
}) => {
  const nonEditableClasses = _.filter(
    currentSharedDetails,
    cls => !_.includes(editableClassIds, cls?.class?.id)
  );

  return [...nonEditableClasses, ...newSharedDetails];
};

export const shareCourseFlowContent = ({
  id,
  action = SHARE_ACTIONS.SHARE,
  sharingState = SHARING_STATES.PUBLISHED,
  addedClasses = [],
  deletedClasses = [],
  updatedClasses = [],
  shareParentOnly = true,
  associatedClasses = [],
  editableClasses = [],
  currItem: _currItem,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo();
    const userId = _.get(userInfo, "id", "");
    const learningCourseId = getSelectedLearningCourseId(state);

    const variables = {
      input: {
        id,
        learningCourseId,
        action,
        addedClasses,
        deletedClasses,
        updatedClasses,
        state: sharingState,
        shareParentOnly,
      },
    };

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    const currentViewType =
      _.get(state, "learningCourse.currentCourseViewType") ||
      _.get(state, "unitPlans.currentViewType");

    const isFlowView = _.includes([FLOW, COURSE_FLOW], currentViewType);

    try {
      const currItem = isFlowView
        ? getCourseFlowItemFromCache({
            id,
            variables: feedQueryVariables,
          }) || _currItem
        : _currItem;

      const itemOldSharingState = _.get(
        currItem,
        "state",
        SHARING_STATES.DRAFT
      );

      const isUnitOrSection = isModule(currItem);
      const isUnit = isUnitPlan(currItem);
      const unitPlanId = _.get(currItem, "item.id", "");

      if (isUnit && unitPlanId) {
        variables.input.updateUnitPlan = {
          isSharedWithStudents: true,
          isSharedWithFamilies: true,
          unitPlanId,
          updatedBy: userId,
        };
      }

      let resourceType;

      switch (true) {
        case isUnit:
          resourceType = GENERIC_ITEM_TYPES.UNIT_PLAN;
          break;
        case isFolder(currItem):
          resourceType = GENERIC_ITEM_TYPES.FOLDER;
          break;
        case isAssessment(currItem):
          resourceType = GENERIC_ITEM_TYPES.ASSESSMENT;
          break;
        default:
          resourceType = GENERIC_ITEM_TYPES.RESOURCE;
      }

      let itemSharingState = sharingState;
      let visibilityStats = true;

      // First, update individual items in cache
      const isSharing = action === SHARE_ACTIONS.SHARE;
      const currentSharedDetails = _.get(currItem, "sharedDetails", []);

      const updatedSharedDetails = isSharing
        ? updateSharedDetails({
            currentSharedDetails,
            addedClasses,
            updatedClasses,
            deletedClasses,
          })
        : [];

      itemSharingState =
        _.size(updatedSharedDetails) > 0 ? sharingState : SHARING_STATES.DRAFT;

      const newItem = {
        ...currItem,
        state: itemSharingState,
        sharedDetails: updatedSharedDetails,
      }; // Get course flow feed from cache
      const cachedData = getEntireCourseFlowFeedQueryFromCache(
        feedQueryVariables
      );

      // Update unit plan feed cache directly (card / table / timeline views)
      if (!isFlowView) {
        const unitPlanFeedVars = dispatch(
          getLearningCourseUnitPlanFeedQueryVariables()
        );

        const originalUnitPlanCachedData = cache.readQuery({
          query: getLearningCourseUnitPlanFeedQuery,
          variables: unitPlanFeedVars,
        });

        const unitPlanCachedData = _.cloneDeep(originalUnitPlanCachedData);

        let didOptimisticUpdate = false;

        if (unitPlanCachedData) {
          const unitPlanEdges = _.get(
            unitPlanCachedData,
            "node.unitPlans.edges",
            []
          );
          const unitPlanIdx = _.findIndex(
            unitPlanEdges,
            edge => _.get(edge, "node.learningCourseFlowResource.id") === id
          );

          if (unitPlanIdx !== -1) {
            // Enrich class objects with title/profileImage from associatedClasses.
            // getSharedClassStudentStructure only keeps class.id, but the unit plan
            // feed fragment requests class { id title profileImage }.
            const enrichedSharedDetails = _.map(
              updatedSharedDetails,
              detail => {
                const classInfo = _.find(associatedClasses, {
                  id: detail.class.id,
                });
                return {
                  ...detail,
                  class: {
                    ...detail.class,
                    title: _.get(classInfo, "title", null),
                    profileImageData: _.get(
                      classInfo,
                      "profileImageData",
                      null
                    ),
                  },
                };
              }
            );

            // Optimistically write updated state + sharedDetails into the unit plan feed
            cache.writeQuery({
              query: getLearningCourseUnitPlanFeedQuery,
              variables: unitPlanFeedVars,
              data: update(unitPlanCachedData, {
                node: {
                  unitPlans: {
                    edges: {
                      [unitPlanIdx]: {
                        node: {
                          isSharedWithFamilies: { $set: true },
                          isSharedWithStudents: { $set: true },
                          learningCourseFlowResource: {
                            state: { $set: itemSharingState },
                            sharedDetails: { $set: enrichedSharedDetails },
                          },
                        },
                      },
                    },
                  },
                },
              }),
            });

            didOptimisticUpdate = true;
          }
        }

        const hasLockedGradingPeriodAssessment = _.get(
          currItem,
          "item.hasLockedGradingPeriodAssessment",
          false
        );

        try {
          await client.mutate({
            mutation: shareCourseFlowResourceMutation,
            variables,
          });
          dispatch(
            setToastMsg({
              msg: getSharingToastMsg({
                action,
                newSharingState: itemSharingState,
                oldSharingState: itemOldSharingState,
                visibilityStats,
                addedClasses,
                deletedClasses,
                updatedClasses,
                shareParentOnly,
                resourceType,
                associatedClasses,
                hasLockedAcademicTermChildren:
                  hasLockedGradingPeriodAssessment && !shareParentOnly,
              }),
              notString: true,
              type: "success",
              position: "toast-bottom-left",
            })
          );
        } catch (e) {
          if (didOptimisticUpdate) {
            // Revert the optimistic cache update
            cache.writeQuery({
              query: getLearningCourseUnitPlanFeedQuery,
              variables: unitPlanFeedVars,
              data: originalUnitPlanCachedData,
            });
          }
          console.error(e);
          dispatchError({ dispatch, e });
        }
        return;
      }

      // Save reference to original cached data for rollback purposes (no need to clone)
      const originalCachedData = cachedData;

      // Save reference to original item for rollback purposes (no need to clone)
      const originalItem = currItem;

      // Create a list to track all modified child items for potential rollback
      const modifiedChildItems = [];

      const courseFlowFeed = getCourseFlowFeed(cachedData);
      if (_.isEmpty(courseFlowFeed)) return;

      const itemIdx = _.findIndex(
        courseFlowFeed.edges,
        ({ node }) => node.id === id
      );

      if (itemIdx === -1) return;

      // Update the sharing state of the item in the whole feed
      let updatedFeed = update(courseFlowFeed, {
        edges: {
          [itemIdx]: {
            node: {
              state: { $set: itemSharingState },
            },
          },
        },
      });

      // Update the sharing state and sharing settings of the item
      writeCourseFlowItemInCache({
        id,
        variables: feedQueryVariables,
        data: newItem,
      });

      const courseFlowFeedNodes = _.map(updatedFeed.edges, "node");

      let hasLockedAcademicTermChildren = false;

      // Update the sharing state/settings of the children
      if (isUnitOrSection) {
        if (!shareParentOnly) {
          const editableClassIds = _.map(editableClasses, "id");
          const newEditableClassesForChildren = _.filter(
            updatedSharedDetails,
            cls => _.includes(editableClassIds, cls?.class?.id)
          );

          const nestedChildren = getNestedChildren({
            items: courseFlowFeedNodes,
            id,
          });
          // Update the sharing state of children
          _.forEach(nestedChildren, child => {
            const academicTerm = _.get(child, "item.academicTerm");
            const assessmentType = _.get(child, "item.assessmentType.value");
            const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
              academicTerm,
              assessmentType
            );
            hasLockedAcademicTermChildren =
              hasLockedAcademicTermChildren || !isGradingPeriodUnlocked;

            // If grading period is locked then the item's shared settings cannot be updated
            if (!isGradingPeriodUnlocked) return;

            const childItem = getCourseFlowItemFromCache({
              id: child.id,
              variables: feedQueryVariables,
            });

            // Save original child item for potential rollback
            modifiedChildItems.push({
              id: child.id,
              originalData: childItem,
            });

            const childIndex = _.findIndex(
              courseFlowFeed.edges,
              ({ node }) => node.id === child.id
            );

            const childSharedDetails = _.get(childItem, "sharedDetails", []);

            const updatedChildSharedDetails = getUpdatedChildrenSharingState({
              currentSharedDetails: childSharedDetails,
              newSharedDetails: newEditableClassesForChildren,
              editableClassIds,
            });

            const childCurrentSharingState = _.get(
              childItem,
              "state",
              SHARING_STATES.DRAFT
            );

            const childSharingState =
              _.size(updatedChildSharedDetails) > 0
                ? isSharing
                  ? SHARING_STATES.PUBLISHED
                  : childCurrentSharingState
                : SHARING_STATES.DRAFT;

            const childNewItem = {
              ...childItem,
              state: childSharingState,
              sharedDetails: updatedChildSharedDetails,
            };

            // Update the sharing state of the child item in the whole feed
            updatedFeed = update(updatedFeed, {
              edges: {
                [childIndex]: {
                  node: {
                    state: { $set: childSharingState },
                  },
                },
              },
            });

            writeCourseFlowItemInCache({
              id: child.id,
              variables: feedQueryVariables,
              data: childNewItem,
            });
          });
        }
      }

      if (itemSharingState === SHARING_STATES.PUBLISHED) {
        const ancestors = getAncestors({
          items: courseFlowFeedNodes,
          id,
        });

        const ancestorsWithSharingDetails = _.map(ancestors, ancestor =>
          getCourseFlowItemFromCache({
            id: ancestor.id,
            variables: feedQueryVariables,
          })
        );

        visibilityStats = getIsResourceSharedRecursivelyWithSameSettings({
          orgResource: newItem,
          items: ancestorsWithSharingDetails,
        });
      }

      // Update the whole feed in cache
      cache.writeQuery({
        query: getEntireCourseFlowFeedQuery,
        variables: feedQueryVariables,
        data: update(cachedData, {
          node: {
            learningCourseFlowFeedWrapper: {
              learningCourseFlowFeed: {
                edges: { $set: updatedFeed.edges },
              },
            },
          },
        }),
      });

      // [Don't remove] Hack to update the shared details in the UI
      dispatch(setActiveContentData({}));

      try {
        // Now perform the actual mutation
        await client.mutate({
          mutation: shareCourseFlowResourceMutation,
          variables,
          optimisticResponse: {
            __typename: "Mutation",
            planner: {
              shareLearningCourseFlowResource: true,
              __typename: "PlannerMutations",
            },
          },
        });
        dispatch(
          setToastMsg({
            msg: getSharingToastMsg({
              action,
              newSharingState: itemSharingState,
              oldSharingState: itemOldSharingState,
              visibilityStats,
              addedClasses,
              deletedClasses,
              updatedClasses,
              shareParentOnly,
              resourceType,
              associatedClasses,
              hasLockedAcademicTermChildren,
            }),
            notString: true,
            type: "success",
            position: "toast-bottom-left",
          })
        );
      } catch (error) {
        // Revert item cache
        writeCourseFlowItemInCache({
          id,
          variables: feedQueryVariables,
          data: originalItem,
        });

        // Revert all child items
        _.forEach(modifiedChildItems, ({ id, originalData }) => {
          writeCourseFlowItemInCache({
            id,
            variables: feedQueryVariables,
            data: originalData,
          });
        });

        // Revert the feed query cache
        cache.writeQuery({
          query: getEntireCourseFlowFeedQuery,
          variables: feedQueryVariables,
          data: originalCachedData,
        });

        console.error(error);
        dispatchError({ dispatch, e: error });
      }
      return;
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

export const updateUnitPlanVisibilty = ({ input = {} }) => {
  return async dispatch => {
    const variables = {
      input,
    };

    try {
      await client.mutate({
        mutation: updateUnitPlanSharingMutation,
        variables,
      });

      const t = i18n.t;

      dispatch(
        setToastMsg({
          msg: t("course:unit_access_updated"),
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );

      dispatch;
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

const getLockingActionToastMsg = ({
  isUnit,
  isLocked,
  updateSelf,
  updateChildren,
  entityLabelLocale = "common:resource_label",
  hasConflict = false,
}) => {
  const t = i18n.t;
  let locale = "course:locking_action_item_toast";
  const lockingAction = isLocked ? t("common:locked") : t("common:unlocked");
  let entityType = t(entityLabelLocale),
    subEntity = t("course:all_items");

  if (updateSelf && updateChildren) {
    locale = "course:locking_action_item_with_children_toast";
    if (isUnit) {
      subEntity = t("unitPlan:unit_flow");
    }
  } else if (!updateSelf && updateChildren) {
    locale = "course:locking_action_children_toast";
    if (isUnit) {
      entityType = t("unitPlan:unit_flow");
    }
  }

  if (isLocked && getIsBlueprintSyncHistoryV2Enabled() && hasConflict) {
    locale = `${locale}_skip`;
  }

  return t(locale, {
    lockingAction,
    entityType,
    subEntity,
  });
};

export const updateCourseFlowItemsLockStatus = ({ resourcesData }) => {
  return (dispatch, getState) => {
    try {
      const state = getState();
      const feedQueryVariables = getCourseFlowFeedQueryVariables({ state });

      const cachedData = getEntireCourseFlowFeedQueryFromCache(
        feedQueryVariables
      );
      if (!cachedData) return;

      const courseFlowFeed = getCourseFlowFeed(cachedData);
      if (_.isEmpty(courseFlowFeed)) return;

      let updatedFeed = courseFlowFeed;

      _.forEach(resourcesData, ({ entityId, isLocked }) => {
        const lockingInfo = { isLocked };

        const itemIdx = _.findIndex(
          courseFlowFeed.edges,
          ({ node }) =>
            _.get(node, "item.id") === entityId || node.id === entityId
        );

        if (itemIdx === -1) return;

        const nodeId = courseFlowFeed.edges[itemIdx].node.id;

        const currItem = getCourseFlowItemFromCache({
          id: nodeId,
          variables: feedQueryVariables,
        });

        if (!currItem) return;

        const isItemUnitOrAssessment =
          isUnitPlan(currItem) || isAssessment(currItem);

        const updatedItem = update(currItem, {
          ...(isItemUnitOrAssessment
            ? { item: { lockingInfo: { $set: lockingInfo } } }
            : { lockingInfo: { $set: lockingInfo } }),
        });

        updatedFeed = update(updatedFeed, {
          edges: {
            [itemIdx]: {
              node: {
                ...(isItemUnitOrAssessment
                  ? { item: { lockingInfo: { $set: lockingInfo } } }
                  : { lockingInfo: { $set: lockingInfo } }),
              },
            },
          },
        });

        writeCourseFlowItemInCache({
          id: nodeId,
          variables: feedQueryVariables,
          data: updatedItem,
        });
      });

      cache.writeQuery({
        query: getEntireCourseFlowFeedQuery,
        variables: feedQueryVariables,
        data: update(cachedData, {
          node: {
            learningCourseFlowFeedWrapper: {
              learningCourseFlowFeed: {
                edges: { $set: updatedFeed.edges },
              },
            },
          },
        }),
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const updateLockedState = ({ type, id, assessmentTypes }) => {
  if (_.isEmpty(type) || _.isEmpty(id)) return;
  return async (dispatch, getState) => {
    const t = i18n.t;
    let entityId = id;
    const state = getState();
    const learningCourseId = getSelectedLearningCourseId(state);

    let lockScope = LOCKING_SCOPE.SELF,
      entityType = "LEARNING_COURSE_FLOW";

    const isLocked = !_.includes(type, "UNLOCK");

    switch (type) {
      case LOCKING_ACTIONS.LOCK_UNIT_AND_ITEMS:
      case LOCKING_ACTIONS.UNLOCK_UNIT_AND_ITEMS:
      case LOCKING_ACTIONS.LOCK_SECTION_AND_ITEMS:
      case LOCKING_ACTIONS.UNLOCK_SECTION_AND_ITEMS: {
        lockScope = LOCKING_SCOPE.SELF_AND_CHILDREN;
        break;
      }

      case LOCKING_ACTIONS.LOCK_ITEMS_ONLY:
      case LOCKING_ACTIONS.UNLOCK_ITEMS_ONLY: {
        lockScope = LOCKING_SCOPE.CHILDREN;
        break;
      }

      default: {
        lockScope = LOCKING_SCOPE.SELF;
      }
    }

    // This will be used for optimistic updates
    let updateSelf = _.includes(lockScope, LOCKING_SCOPE.SELF);
    const updateChildren = _.includes(lockScope, LOCKING_SCOPE.CHILDREN);

    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    try {
      const currItem = getCourseFlowItemFromCache({
        id,
        variables: feedQueryVariables,
      });

      const isUnitOrSection = isModule(currItem);
      const isUnit = isUnitPlan(currItem);

      switch (true) {
        case isUnit:
          entityType = GENERIC_ITEM_TYPES.UNIT_PLAN;
          entityId = currItem?.item?.id;
          break;
        case isAssessment(currItem):
          entityType = GENERIC_ITEM_TYPES.ASSESSMENT;
          entityId = currItem?.item?.id;
          break;
        default:
          entityType = "LEARNING_COURSE_FLOW";
      }

      let updatedItem = currItem;

      const isItemConflicted = getHasConflict({ item: currItem });
      let hasAnyConflict = isItemConflicted;

      if (!getIsBlueprintSyncHistoryV2Enabled()) {
        updateSelf &&= !isItemConflicted || !isLocked;
      }

      // If neither the item nor its children need updating, exit early
      if (!updateSelf && !updateChildren) return;

      // Get course flow feed from cache
      const cachedData = getEntireCourseFlowFeedQueryFromCache(
        feedQueryVariables
      );

      if (!cachedData) return;

      // Save reference to original cached data for potential rollback (no need to clone)
      const originalCachedData = cachedData;

      // Save reference to original item for potential rollback (no need to clone)
      const originalItem = currItem;

      // Create a list to track all modified child items for potential rollback
      const modifiedChildItems = [];

      const courseFlowFeed = getCourseFlowFeed(cachedData);
      if (_.isEmpty(courseFlowFeed)) return;

      const itemIdx = _.findIndex(
        courseFlowFeed.edges,
        ({ node }) => node.id === id
      );

      if (itemIdx === -1) return;

      let updatedFeed = courseFlowFeed;

      const lockingInfo = { isLocked };

      if (updateSelf) {
        const isItemUnitOrAssessment =
          isUnitPlan(updatedItem) || isAssessment(updatedItem);

        if (isItemUnitOrAssessment) {
          updatedItem = update(updatedItem, {
            item: {
              lockingInfo: { $set: lockingInfo },
            },
          });
        } else {
          updatedItem = update(updatedItem, {
            lockingInfo: { $set: lockingInfo },
          });
        }

        // Update the lock state of the item in the whole feed
        updatedFeed = update(updatedFeed, {
          edges: {
            [itemIdx]: {
              node: {
                ...(isItemUnitOrAssessment
                  ? {
                      item: {
                        lockingInfo: { $set: lockingInfo },
                      },
                    }
                  : { lockingInfo: { $set: lockingInfo } }),
              },
            },
          },
        });

        // Update the lock state of the item
        writeCourseFlowItemInCache({
          id,
          variables: feedQueryVariables,
          data: updatedItem,
        });
      }

      const courseFlowFeedNodes = _.map(updatedFeed.edges, "node");

      if (isUnitOrSection && updateChildren) {
        const nestedChildren = getNestedChildren({
          items: courseFlowFeedNodes,
          id,
        });

        // Update the lock state of children
        _.forEach(nestedChildren, child => {
          const childItem = getCourseFlowItemFromCache({
            id: child.id,
            variables: feedQueryVariables,
          });

          if (!childItem) return;

          const isChildItemConflicted = getHasConflict({ item: childItem });
          if (isChildItemConflicted) hasAnyConflict = true;

          if (!getIsBlueprintSyncHistoryV2Enabled() && isChildItemConflicted) {
            return;
          }

          // Save original child item for potential rollback
          modifiedChildItems.push({
            id: child.id,
            originalData: childItem,
          });

          const childIndex = _.findIndex(
            courseFlowFeed.edges,
            ({ node }) => node.id === child.id
          );

          const isChildItemAssessment = isAssessment(childItem);

          const childNewItem = update(childItem, {
            ...(isChildItemAssessment
              ? {
                  item: {
                    lockingInfo: { $set: lockingInfo },
                  },
                }
              : { lockingInfo: { $set: lockingInfo } }),
          });

          // Update the sharing state of the child item in the whole feed
          updatedFeed = update(updatedFeed, {
            edges: {
              [childIndex]: {
                node: {
                  ...(isChildItemAssessment
                    ? {
                        item: {
                          lockingInfo: { $set: lockingInfo },
                        },
                      }
                    : { lockingInfo: { $set: lockingInfo } }),
                },
              },
            },
          });

          writeCourseFlowItemInCache({
            id: child.id,
            variables: feedQueryVariables,
            data: childNewItem,
          });
        });
      }

      const updatedData = update(cachedData, {
        node: {
          learningCourseFlowFeedWrapper: {
            learningCourseFlowFeed: {
              edges: { $set: updatedFeed.edges },
            },
          },
        },
      });

      // Update the whole feed in cache
      cache.writeQuery({
        query: getEntireCourseFlowFeedQuery,
        variables: feedQueryVariables,
        data: updatedData,
      });

      // [Don't remove] Hack to update the lock state in the UI
      dispatch(setActiveContentData({}));

      try {
        // Now perform the actual mutation
        const variables = {
          input: {
            learningCourseId,
            lockType: "CONTENT_LEVEL_LOCK",
            lockScope,
            entityLocks: [
              {
                entityId,
                entityType,
                isLocked,
              },
            ],
          },
        };

        await client.mutate({
          mutation: updateLockedStateMutation,
          variables,
          optimisticResponse: {
            __typename: "Mutation",
            planner: {
              bulkUpdateLearningCourseEntityLock: {
                isSuccess: true,
              },
              __typename: "PlannerMutations",
            },
          },
        });

        const entityLabelLocale = getItemTypeLocale({
          item: currItem,
          assessmentTypes,
        });

        dispatch(
          setToastMsg({
            msg: getLockingActionToastMsg({
              isUnit,
              isLocked,
              updateSelf,
              updateChildren,
              entityLabelLocale,
              hasConflict: hasAnyConflict,
            }),
            notString: true,
            type: "success",
            position: "toast-bottom-left",
          })
        );
      } catch (error) {
        // Revert item cache
        writeCourseFlowItemInCache({
          id,
          variables: feedQueryVariables,
          data: originalItem,
        });

        // Revert all child items
        _.forEach(modifiedChildItems, ({ id, originalData }) => {
          writeCourseFlowItemInCache({
            id,
            variables: feedQueryVariables,
            data: originalData,
          });
        });

        // Revert the feed query cache
        cache.writeQuery({
          query: getEntireCourseFlowFeedQuery,
          variables: feedQueryVariables,
          data: originalCachedData,
        });

        console.error(error);
        dispatchError({
          dispatch,
          e: error,
          customMessage: t("course:locking_action_error_toast", {
            entityType: t(
              getItemTypeLocale({ item: currItem, assessmentTypes })
            ),
            lockingAction: isLocked ? t("common:locked") : t("common:unlocked"),
          }),
          notString: true,
        });
      }
      return;
    } catch (error) {
      console.error(error);
      dispatchError({
        dispatch,
        e: error,
        customMessage: t("course:locking_action_error_toast", {
          entityType: t("common:resource_label"),
          lockingAction: isLocked ? t("common:locked") : t("common:unlocked"),
        }),
        notString: true,
      });
    }
  };
};

/**
 * This function is used to fix the depth of nodes in the course flow
 *
 * In course flow, unit plans are at depth 1, and their children are at depth 2+
 * But if we navigate to unit flow, unit plan does not exist and children start from depth 1
 * When we navigate back to course flow, the depth of children is incorrect in cache
 */
export const fixDepthOfNodes = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const feedQueryVariables = getCourseFlowFeedQueryVariables({
      state,
    });

    cache.updateQuery(
      {
        query: getEntireCourseFlowFeedQuery,
        variables: feedQueryVariables,
      },
      data => {
        if (!data) return;

        const courseFlowFeed = _.cloneDeep(getCourseFlowFeed(data));
        const courseFlowFeedNodes = _.map(courseFlowFeed.edges, "node");

        const unitPlans = _.filter(courseFlowFeedNodes, node =>
          isUnitPlan(node)
        );

        _.forEach(unitPlans, unitPlan => {
          const unitPlanChildren = _.filter(courseFlowFeedNodes, node => {
            return node.parent?.id === unitPlan.id;
          });

          _.forEach(unitPlanChildren, child => {
            // the difference between depths of parent and immediate children
            // can never be greater than 1
            const isDepthIncorrect = child.depth - unitPlan.depth !== 1;

            if (isDepthIncorrect) {
              updateDepthRecursively({
                items: courseFlowFeed.edges,
                id: child.id,
                factor: 1,
              });
            }
          });
        });

        return update(data, {
          node: {
            [getWrapperFieldName()]: {
              learningCourseFlowFeed: {
                edges: { $set: courseFlowFeed.edges },
              },
            },
          },
        });
      }
    );
  };
};

const initialState = {
  assignmentModalData: {},
  assessmentSelectModalData: {},
  assessmentImportModalData: {},
  importUnitFromLibraryModalData: {},
  importUnitFromToddleLibraryModalData: {},
  importFromAnotherCourseModalData: {},
  importFromAnotherCourseClassModalData: {},
  addNoteModalData: {},
  worksheetModalData: {},
  addLinkModalData: {},
  activeContentData: {},
  isActionLoading: false,
  displaySettings: {
    show_outline: true,
    show_blueprint_lock: true,
    collapse_all_sections: false,
    collapse_expand_action_nonce: 0,
    expand_all_sections: true,
    thumbnail_image: true,
    task_resource_type: false,
    task_status: true,
    grading_period: true,
    assessment_category: true,
    add_to_top: false,
    add_to_bottom: true,
  },
  filters: {
    searchText: "",
    subjects: [],
    unitType: [],
    students: [],
    contentTypes: [],
    classes: [],
    taskTypeIds: [],
  },
  isDragDisabled: false,
  moveToSectionModalData: {},
  confirmationDialogData: {},
  isNavigatingFromUnitFlow: false,
};

export const setAssignmentModalData = data => ({
  type: SET_ASSIGNMENT_MODAL_DATA,
  data,
});

export const setAssessmentImportModalData = data => ({
  type: SET_ASSESSMENT_IMPORT_MODAL_DATA,
  data,
});

export const setAssessmentSelectModalData = data => ({
  type: SET_ASSESSMENT_SELECT_MODAL_DATA,
  data,
});

export const setImportFromAnotherCourseModalData = data => {
  return {
    type: SET_IMPORT_FROM_ANOTHER_CLASS,
    data,
  };
};

export const setImportFromAnotherCourseClassModalData = data => ({
  type: SET_IMPORT_FROM_ANOTHER_COURSE_CLASS_MODAL,
  data,
});

export const setImportFromAnotherClassFilters = data => {
  return {
    type: SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS,
    data,
  };
};

export const setImportUnitFromLibraryModalData = data => {
  return {
    type: SET_IMPORT_UNIT_FROM_LIBRARY,
    data,
  };
};

export const setImportUnitFromToddleLibraryModalData = data => {
  return {
    type: SET_IMPORT_UNIT_FROM_TODDLE_LIBRARY,
    data,
  };
};

export const setAddNoteModalData = data => ({
  type: SET_ADD_NOTE_MODAL_DATA,
  data,
});

export const setAddLinkModalData = data => ({
  type: SET_ADD_LINK_MODAL_DATA,
  data,
});

export const setActiveContentData = data => ({
  type: SET_ACTIVE_CONTENT_DATA,
  data,
});

export const updateDisplaySettings = data => {
  return {
    type: UPDATE_DISPLAY_SETTINGS,
    data,
  };
};

export const updateFilters = data => {
  return {
    type: UPDATE_FILTERS,
    data,
  };
};

export const resetFilters = () => ({
  type: RESET_FILTERS,
});

export const setIsActionLoading = data => ({
  type: SET_ACTION_LOADING,
  data,
});

export const setMoveToSectionModalData = data => ({
  type: SET_MOVE_TO_SECTION_MODAL_DATA,
  data,
});

export const setConfirmationDialogData = data => ({
  type: SET_CONFIRM_MODAL_DATA,
  data,
});

export const setIsNavigatingFromUnitFlow = data => ({
  type: SET_IS_NAVIGATING_FROM_UNIT_FLOW,
  data,
});

export const setWorksheetModalData = data => ({
  type: SET_WORKSHEET_MODAL_DATA,
  data,
});

const REDUCER_HANDLERS = {
  [SET_ASSIGNMENT_MODAL_DATA]: (state, action) => {
    return update(state, { assignmentModalData: { $set: action.data } });
  },
  [SET_ASSESSMENT_IMPORT_MODAL_DATA]: (state, action) => {
    return update(state, { assessmentImportModalData: { $set: action.data } });
  },
  [SET_ASSESSMENT_SELECT_MODAL_DATA]: (state, action) => {
    return update(state, { assessmentSelectModalData: { $set: action.data } });
  },
  [SET_IMPORT_FROM_ANOTHER_CLASS]: (state, action) => {
    return update(state, {
      importFromAnotherCourseModalData: { $set: action.data },
    });
  },
  [SET_IMPORT_FROM_ANOTHER_COURSE_CLASS_MODAL]: (state, action) => {
    return update(state, {
      importFromAnotherCourseClassModalData: { $set: action.data },
    });
  },
  [SET_IMPORT_UNIT_FROM_LIBRARY]: (state, action) => {
    return update(state, {
      importUnitFromLibraryModalData: { $set: action.data },
    });
  },
  [SET_IMPORT_UNIT_FROM_TODDLE_LIBRARY]: (state, action) => {
    return update(state, {
      importUnitFromToddleLibraryModalData: { $set: action.data },
    });
  },
  [SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS]: (state, action) => {
    const data = {
      ..._.get(state, "importFromAnotherCourseModalData.filters", {}),
      ..._.get(action, "data", {}),
    };

    return update(state, {
      importFromAnotherCourseModalData: {
        filters: { $set: data },
      },
    });
  },
  [SET_ADD_NOTE_MODAL_DATA]: (state, action) => {
    return update(state, { addNoteModalData: { $set: action.data } });
  },
  [SET_ADD_LINK_MODAL_DATA]: (state, action) => {
    return update(state, { addLinkModalData: { $set: action.data } });
  },
  [SET_WORKSHEET_MODAL_DATA]: (state, action) => {
    return update(state, { worksheetModalData: { $set: action.data } });
  },
  [SET_ACTIVE_CONTENT_DATA]: (state, action) => {
    return update(state, { activeContentData: { $set: action.data } });
  },
  [UPDATE_DISPLAY_SETTINGS]: (state, action) => {
    return update(state, { displaySettings: { $set: action.data } });
  },
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;
    _.map(Object.keys(params), key => {
      state = update(state, {
        filters: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [RESET_FILTERS]: state => {
    return update(state, {
      filters: { $set: initialState.filters },
    });
  },
  [RESET_MODAL_STATE]: () => {
    return initialState;
  },
  [SET_ACTION_LOADING]: (state, action) => {
    return update(state, { isActionLoading: { $set: action.data } });
  },
  [SET_MOVE_TO_SECTION_MODAL_DATA]: (state, action) => {
    return update(state, { moveToSectionModalData: { $set: action.data } });
  },
  [SET_CONFIRM_MODAL_DATA]: (state, action) => {
    return update(state, { confirmationDialogData: { $set: action.data } });
  },
  [SET_IS_DRAG_DISABLED]: (state, action) => {
    return update(state, { isDragDisabled: { $set: action.data } });
  },
  [SET_IS_NAVIGATING_FROM_UNIT_FLOW]: (state, action) => {
    return update(state, { isNavigatingFromUnitFlow: { $set: action.data } });
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
